// Copyright 2025, University of Maryland, All Rights Reserved
import { ColumnDef } from "@tanstack/react-table"
import { Button } from "@/components/ui/button"
import { ArrowUpDown, ExternalLink, FileText, AlertTriangle } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { type DatasourceBill, type DatasourceBillVersion } from '@/types/datasource-bill'

interface GetDatasourceBillsColumnsProps {
  onViewVersions?: (bill: DatasourceBill) => void
}

export function getDatasourceBillsColumns({
  onViewVersions
}: GetDatasourceBillsColumnsProps = {}): ColumnDef<DatasourceBill>[] {

  const formatArrivalTime = (arrivalTime?: string) => {
    if (!arrivalTime) return '—'
    
    try {
      const date = new Date(arrivalTime)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return '—'
    }
  }

  return [
    {
      accessorKey: "bill_number",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-auto p-0 font-semibold hover:bg-transparent"
          >
            Bill Number
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        )
      },
      cell: ({ row }) => {
        const bill = row.original
        return (
          <div className="font-mono font-medium">
            {bill.bill_number}
          </div>
        )
      },
    },
    {
      accessorKey: "title",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-auto p-0 font-semibold hover:bg-transparent"
          >
            Title
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        )
      },
      cell: ({ row }) => {
        const bill = row.original
        return (
          <div className="max-w-[300px]">
            <div className="font-medium truncate" title={bill.title}>
              {bill.title}
            </div>
            {bill.synopsis && (
              <div className="text-sm text-muted-foreground line-clamp-2 mt-1" title={bill.synopsis}>
                {bill.synopsis}
              </div>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: "crossfile_bill_number",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-auto p-0 font-semibold hover:bg-transparent"
          >
            Cross-file
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        )
      },
      cell: ({ row }) => {
        const crossfile = row.original.crossfile_bill_number
        return crossfile ? (
          <span className="font-mono text-sm">{crossfile}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
      },
    },
    {
      accessorKey: "arrival_time",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-auto p-0 font-semibold hover:bg-transparent"
          >
            Arrival Time
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        )
      },
      cell: ({ row }) => {
        const bill = row.original
        return (
          <div className="text-sm text-muted-foreground">
            {formatArrivalTime(bill.arrival_time)}
          </div>
        )
      },
      sortingFn: (rowA, rowB) => {
        const dateA = rowA.original.arrival_time ? new Date(rowA.original.arrival_time).getTime() : 0
        const dateB = rowB.original.arrival_time ? new Date(rowB.original.arrival_time).getTime() : 0
        return dateA - dateB
      },
    },
    {
      accessorKey: "latest_version_arrival",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-auto p-0 font-semibold hover:bg-transparent"
          >
            Amendment Date
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        )
      },
      cell: ({ row }) => {
        const bill = row.original
        return (
          <div className="text-sm text-muted-foreground">
            {formatArrivalTime(bill.latest_version_arrival)}
          </div>
        )
      },
      sortingFn: (rowA, rowB) => {
        const dateA = rowA.original.latest_version_arrival ? new Date(rowA.original.latest_version_arrival).getTime() : 0
        const dateB = rowB.original.latest_version_arrival ? new Date(rowB.original.latest_version_arrival).getTime() : 0
        return dateA - dateB
      },
    },
    {
      accessorKey: "versions",
      header: "Versions",
      enableSorting: false,
      cell: ({ row }) => {
        const bill = row.original
        const versions = bill.versions || []
        const hasAmendments = versions.some(v => v.amendments_detected)
        
        return (
          <div className="flex items-center justify-center">
            {onViewVersions ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onViewVersions(bill)
                }}
                title="View Versions & Amendments"
                className="p-1"
              >
                <Badge variant="outline" className="cursor-pointer hover:bg-accent flex items-center gap-1">
                  <FileText className="h-3 w-3" />
                  {versions.length}
                  {hasAmendments && <AlertTriangle className="h-3 w-3 text-amber-500" />}
                </Badge>
              </Button>
            ) : (
              <Badge variant="outline" className="flex items-center gap-1">
                <FileText className="h-3 w-3" />
                {versions.length}
                {hasAmendments && <AlertTriangle className="h-3 w-3 text-amber-500" />}
              </Badge>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: "token_count",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-auto p-0 font-semibold hover:bg-transparent"
          >
            Token Count
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        )
      },
      cell: ({ row }) => {
        const bill = row.original
        const latestVersion = bill.versions?.reduce((latest: DatasourceBillVersion | undefined, current) =>
          !latest || current.version > latest.version ? current : latest
        , undefined as DatasourceBillVersion | undefined)

        const tokenCount = latestVersion?.estimated_token_count
        const threshold = 50000 // TOKEN_THRESHOLD from backend

        if (!tokenCount) {
          return (
            <div className="text-center">
              <Badge variant="secondary" className="text-xs">
                Unknown
              </Badge>
            </div>
          )
        }

        const isOverThreshold = tokenCount > threshold
        const formattedCount = tokenCount.toLocaleString()

        return (
          <div className="text-center">
            <Badge
              variant={isOverThreshold ? "destructive" : "default"}
              className={`text-xs font-mono ${
                isOverThreshold
                  ? "bg-red-100 text-red-800 border-red-300 dark:bg-red-900 dark:text-red-100 dark:border-red-700"
                  : "bg-green-100 text-green-800 border-green-300 dark:bg-green-900 dark:text-green-100 dark:border-green-700"
              }`}
              title={`${formattedCount} tokens (threshold: ${threshold.toLocaleString()})`}
            >
              {formattedCount}
            </Badge>
          </div>
        )
      },
      sortingFn: (rowA, rowB) => {
        const getTokenCount = (bill: DatasourceBill) => {
          const latestVersion = bill.versions?.reduce((latest: DatasourceBillVersion | undefined, current) =>
            !latest || current.version > latest.version ? current : latest
          , undefined as DatasourceBillVersion | undefined)
          return latestVersion?.estimated_token_count || 0
        }

        const countA = getTokenCount(rowA.original)
        const countB = getTokenCount(rowB.original)
        return countA - countB
      },
    },
    {
      id: "pdf",
      header: "PDF",
      enableSorting: false,
      cell: ({ row }) => {
        const bill = row.original
        
        return (
          <div className="text-center">
            {bill.pdf_url ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  window.open(bill.pdf_url, '_blank', 'noopener,noreferrer')
                }}
                title="View PDF"
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            ) : (
              <Badge variant="destructive">
                Unavailable
              </Badge>
            )}
          </div>
        )
      },
    },
  ]
}